/**
 * invoiceToReceipt — adapter that turns a persisted Invoice (plus its
 * dependents) into the framework-agnostic ReceiptData consumed by the
 * receipt builder and the A4 PDF template.
 *
 * Handles both SALE and CREDIT_NOTE invoices. For credit notes we surface
 * the absolute refund amounts and use a "Refund" payment label so the
 * printout reads naturally.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice, LineItem, Payment } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import { Customer } from '@/db/models/Customers';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { loadStoreGstIdentity } from '@/gst/storeIdentity';
import { useSettingsStore } from '@/store/useSettingsStore';
import { loadBankDetails } from '../bankDetails';
import { GST_STATE_CODES, stateCodeFromGstin, stateCodeFromName } from '@/reports/gst/gstStates';
import type {
  ReceiptData,
  ReceiptLineItem,
  ReceiptTaxLine,
  ReceiptTaxSummaryRow,
} from '../contracts/ReceiptContracts';

/**
 * Bare state name for a code — "29" → "Karnataka".
 *
 * Deliberately not `stateLabel()`, which returns "29-Karnataka": the invoice
 * renders the name and the code as separate parts ("Karnataka (29)"), so the
 * prefixed form would print the code twice.
 */
const stateNameFromCode = (code: string | null | undefined): string | undefined =>
  (code ? GST_STATE_CODES[code] : undefined) || undefined;

const formatDate = (d: Date): string =>
  d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export async function invoiceToReceipt(invoiceId: string): Promise<ReceiptData> {
  const invoice = await database.get<Invoice>('invoices').find(invoiceId);
  const store = await database.get<Store>('stores').find(invoice.storeId);

  // The merchant carries the e-mail and logo that belong on an A4 header. A
  // store with no merchant row yet (first sync) simply prints without them.
  let merchant: Merchant | null = null;
  try {
    merchant = await database.get<Merchant>('merchants').find(store.merchantId);
  } catch {
    merchant = null;
  }

  const [lineItems, payments] = await Promise.all([
    database
      .get<LineItem>('line_items')
      .query(Q.where('invoice_id', invoiceId), Q.where('is_deleted', false))
      .fetch(),
    database
      .get<Payment>('payments')
      .query(Q.where('invoice_id', invoiceId), Q.where('is_deleted', false))
      .fetch(),
  ]);

  // Product names — bulk-load once.
  const productIds = Array.from(new Set(lineItems.map((li) => li.productId)));
  const productNameById: Record<string, string> = {};
  if (productIds.length > 0) {
    const products = await database
      .get<Product>('products')
      .query(Q.where('id', Q.oneOf(productIds)))
      .fetch();
    for (const p of products) productNameById[p.id] = p.name;
  }

  // Customer
  let customer: Customer | null = null;
  if (invoice.customerId) {
    try {
      customer = await database.get<Customer>('customers').find(invoice.customerId);
    } catch {
      customer = null;
    }
  }

  // Address line — single-line, comma joined, skipping blanks.
  const addressLine = [store.address, store.city, store.state, store.pincode]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');

  // Tax breakdown — collapse line-level CGST/SGST/IGST/CESS into bands.
  let cgst = 0, sgst = 0, igst = 0, cess = 0;
  for (const li of lineItems) {
    cgst += li.cgstAmount;
    sgst += li.sgstAmount;
    igst += li.igstAmount;
    cess += li.cessAmount;
  }
  const taxes: ReceiptTaxLine[] = [];
  if (cgst > 0) taxes.push({ label: 'CGST', amount: cgst });
  if (sgst > 0) taxes.push({ label: 'SGST', amount: sgst });
  if (igst > 0) taxes.push({ label: 'IGST', amount: igst });
  if (cess > 0) taxes.push({ label: 'CESS', amount: cess });

  // Rate-wise summary, keyed by HSN + rate. A tax invoice has to show the
  // taxable value and tax charged per rate band, not just one lump sum —
  // it is also what the buyer's accountant reconciles against GSTR-2B.
  const summaryByKey = new Map<string, ReceiptTaxSummaryRow>();
  for (const li of lineItems) {
    const key = `${li.hsnCode ?? ''}|${li.gstRate}|${li.cessRate}`;
    const row = summaryByKey.get(key) ?? {
      hsn: li.hsnCode || undefined,
      gstRate: li.gstRate,
      cessRate: li.cessRate > 0 ? li.cessRate : undefined,
      taxable: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      cess: 0,
    };
    row.taxable += li.taxableAmount;
    row.cgst += li.cgstAmount;
    row.sgst += li.sgstAmount;
    row.igst += li.igstAmount;
    row.cess += li.cessAmount;
    summaryByKey.set(key, row);
  }
  const taxSummary = Array.from(summaryByKey.values()).sort((a, b) => a.gstRate - b.gstRate);

  // Seller identity. Not read off the store row directly: a single-outlet shop
  // usually registers the GSTIN on the merchant and leaves the store override
  // blank, and billing already resolves place of supply through this helper —
  // the printed invoice has to agree with the tax that was charged.
  const identity = await loadStoreGstIdentity(invoice.storeId);

  // Place of supply. The buyer's GSTIN is the most reliable source — its first
  // two digits ARE the state code — then their stated state, then the seller's
  // (a walk-in is supplied where the shop stands).
  const sellerStateCode = identity.homeStateCode;
  const buyerStateCode = customer
    ? stateCodeFromGstin(customer.gstin) || stateCodeFromName(customer.state)
    : null;
  const posCode = buyerStateCode || sellerStateCode;

  const customerAddress = customer
    ? [customer.address, customer.city, customer.state, customer.pincode]
        .map((s) => (s ?? '').trim())
        .filter(Boolean)
        .join(', ')
    : '';

  // Items — surface a discount % only when the line has one. We derive
  // the percent from the absolute discount vs the gross line value so
  // the printout stays accurate regardless of how the discount was
  // entered (flat rupees vs percent vs combo).
  const items: ReceiptLineItem[] = lineItems.map((li) => {
    const gross = li.unitPrice * li.quantity;
    const pct = gross > 0 && li.discountAmount > 0
      ? Math.round((li.discountAmount / gross) * 1000) / 10
      : 0;
    return {
      // The line's own description wins over the product name. A manual
      // (non-catalog) line points at the store's placeholder product, so the
      // name the cashier typed lives here and nowhere else — without this the
      // bill would print "Manual entry" for every such line.
      name: li.description?.trim() || productNameById[li.productId] || '(item)',
      qty: li.quantity,
      unit: li.unit || undefined,
      rate: li.unitPrice,
      amount: li.totalAmount,
      hsn: li.hsnCode || undefined,
      discountPct: pct > 0 ? pct : undefined,
      taxableValue: li.taxableAmount,
      gstRate: li.gstRate,
      taxAmount: li.cgstAmount + li.sgstAmount + li.igstAmount + li.cessAmount,
    };
  });

  // Payment line — single summary; show change for cash overpay.
  const paymentMethods = payments.map((p) => p.method);
  const paid = payments.reduce((sum, p) => sum + p.amount, 0);
  const change = paid > invoice.totalAmount ? paid - invoice.totalAmount : undefined;
  const paymentLabel = invoice.type === 'CREDIT_NOTE'
    ? `Refund (${paymentMethods.join('/') || 'CASH'})`
    : (paymentMethods.join('/') || 'CASH');

  return {
    storeName: store.name,
    addressLine: addressLine || undefined,
    gstin: identity.gstin || undefined,
    phone: store.phone || undefined,
    stateName: store.state || stateNameFromCode(sellerStateCode),
    stateCode: sellerStateCode || undefined,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: formatDate(new Date(invoice.createdAt)),
    customerName: customer?.name?.trim() || customer?.phone || undefined,
    customer: customer
      ? {
          name: customer.name?.trim() || customer.phone || 'Customer',
          gstin: customer.gstin?.trim() || undefined,
          address: customerAddress || undefined,
          stateName: customer.state?.trim() || stateNameFromCode(buyerStateCode),
          stateCode: buyerStateCode || undefined,
          phone: customer.phone?.trim() || undefined,
        }
      : undefined,
    placeOfSupply: stateNameFromCode(posCode),
    placeOfSupplyCode: posCode || undefined,
    isInterState: invoice.isInterState,
    // No reverse-charge supplies are billable from the POS today; the field is
    // printed as "No" because Rule 46(p) requires the statement either way.
    reverseCharge: false,
    items,
    subtotal: invoice.subtotal,
    discount: invoice.discountAmount > 0 ? invoice.discountAmount : undefined,
    taxes,
    taxSummary,
    roundOff: invoice.roundOff !== 0 ? invoice.roundOff : undefined,
    total: invoice.totalAmount,
    payment: payments.length > 0 ? { method: paymentLabel, paid, change } : undefined,
    dueAmount: invoice.dueAmount > 0 ? invoice.dueAmount : undefined,
    dueDate: invoice.dueDate ? formatDate(new Date(invoice.dueDate)) : undefined,
    notes: invoice.notes || undefined,
    terms: invoice.termsConditions || undefined,
    // Only a contextual annotation here; the normal footer comes from ReceiptConfig.
    footerLine1: invoice.type === 'CREDIT_NOTE' ? 'Credit note against original sale' : undefined,
    // Undefined when the shop has not entered a bank, so the layouts that print
    // the block omit it rather than heading three empty lines.
    bank: loadBankDetails(useSettingsStore.getState()),
    email: merchant?.email?.trim() || undefined,
    logoUrl: merchant?.logoUrl?.trim() || undefined,
  };
}
